// A small ZIP writer (UTF-8 names; stored entries, or DEFLATE through CompressionStream in makeZipAsync) for the
// compliance pack and the tax folder export. Pure and synchronous: the
// archive is built in memory from the files given — documents from the vault, CSV tables and the printable pack — and
// opens with any operating system's built-in unzip. The reader for .xlsx lives in tabular.ts.
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
export interface ZipEntry { name: string; data: Uint8Array | string; date?: Date }
const enc = new TextEncoder();
/** a safe entry name: forward slashes, no leading slash, no «..», no control characters */
export function zipName(name: string): string {
  const parts = String(name).replace(/\\/g, '/').split('/').map((p) => p.replace(/[\u0000-\u001f<>:"|?*]/g, '_').trim()).filter((p) => p && p !== '.' && p !== '..');
  return parts.join('/').slice(0, 200) || 'file';
}
function dosTime(d: Date): { time: number; date: number } {
  const y = Math.max(1980, d.getFullYear());
  return { time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2), date: ((y - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate() };
}
interface ZipFile { name: Uint8Array; data: Uint8Array; crc: number; size: number; method: 0 | 8; dt: { time: number; date: number } }
function prepare(entries: ZipEntry[]): { name: Uint8Array; raw: Uint8Array; crc: number; dt: { time: number; date: number } }[] {
  const used = new Set<string>();
  return entries.map((e) => {
    let name = zipName(e.name);
    // the same name twice gets « (2)», « (3)» before the extension
    for (let i = 2; used.has(name); i++) name = zipName(e.name).replace(/(\.[^./]*)?$/, (ext) => ` (${i})${ext}`);
    used.add(name);
    const raw = typeof e.data === 'string' ? enc.encode(e.data) : e.data;
    return { name: enc.encode(name), raw, crc: crc32(raw), dt: dosTime(e.date ?? new Date()) };
  });
}
/** local headers + data, central directory, end record (no ZIP64: archives up to 4 GB and 65 535 entries) */
function writeZip(files: ZipFile[]): Uint8Array {
  if (files.length > 0xffff) throw new Error('zip: too many entries');
  const local: Uint8Array[] = [], central: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const h = new Uint8Array(30 + f.name.length), dv = new DataView(h.buffer);
    dv.setUint32(0, 0x04034b50, true); dv.setUint16(4, 20, true); dv.setUint16(6, 0x0800, true); dv.setUint16(8, f.method, true);
    dv.setUint16(10, f.dt.time, true); dv.setUint16(12, f.dt.date, true); dv.setUint32(14, f.crc, true);
    dv.setUint32(18, f.data.length, true); dv.setUint32(22, f.size, true); dv.setUint16(26, f.name.length, true); dv.setUint16(28, 0, true);
    h.set(f.name, 30);
    local.push(h, f.data);
    const c = new Uint8Array(46 + f.name.length), cv = new DataView(c.buffer);
    cv.setUint32(0, 0x02014b50, true); cv.setUint16(4, 20, true); cv.setUint16(6, 20, true); cv.setUint16(8, 0x0800, true); cv.setUint16(10, f.method, true);
    cv.setUint16(12, f.dt.time, true); cv.setUint16(14, f.dt.date, true); cv.setUint32(16, f.crc, true); cv.setUint32(20, f.data.length, true); cv.setUint32(24, f.size, true);
    cv.setUint16(28, f.name.length, true); cv.setUint16(30, 0, true); cv.setUint16(32, 0, true); cv.setUint16(34, 0, true); cv.setUint16(36, 0, true); cv.setUint32(38, 0, true); cv.setUint32(42, offset, true);
    c.set(f.name, 46);
    central.push(c);
    offset += h.length + f.data.length;
  }
  const cdSize = central.reduce((s, c) => s + c.length, 0);
  const end = new Uint8Array(22), ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true); ev.setUint16(8, files.length, true); ev.setUint16(10, files.length, true); ev.setUint32(12, cdSize, true); ev.setUint32(16, offset, true);
  const out = new Uint8Array(offset + cdSize + 22);
  let p = 0;
  for (const part of [...local, ...central, end]) { out.set(part, p); p += part.length; }
  return out;
}
/** stored entries (no compression) — synchronous */
export function makeZip(entries: ZipEntry[]): Uint8Array {
  return writeZip(prepare(entries).map((f) => ({ name: f.name, data: f.raw, crc: f.crc, size: f.raw.length, method: 0, dt: f.dt })));
}
/** raw DEFLATE (RFC 1951) with the platform's CompressionStream; null where it is not available */
export async function deflateRaw(data: Uint8Array): Promise<Uint8Array | null> {
  const CS = (globalThis as { CompressionStream?: new (format: string) => TransformStream<Uint8Array, Uint8Array> }).CompressionStream;
  if (typeof CS !== 'function' || typeof Blob === 'undefined' || typeof Response === 'undefined') return null;
  try {
    const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CS('deflate-raw') as unknown as TransformStream<Uint8Array, Uint8Array>);
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch { return null; }
}
/**
 * v18 (tax folder export): the same archive with DEFLATE where the browser/Node has CompressionStream('deflate-raw')
 * and it makes the entry smaller; otherwise STORE. Already-compressed files (JPEG, PNG, HEIC, most PDFs) usually stay
 * stored. `compress: false` forces STORE.
 */
export async function makeZipAsync(entries: ZipEntry[], o: { compress?: boolean } = {}): Promise<Uint8Array> {
  const files: ZipFile[] = [];
  for (const f of prepare(entries)) {
    const packed = o.compress === false || f.raw.length < 64 ? null : await deflateRaw(f.raw);
    files.push(packed && packed.length < f.raw.length ? { name: f.name, data: packed, crc: f.crc, size: f.raw.length, method: 8, dt: f.dt } : { name: f.name, data: f.raw, crc: f.crc, size: f.raw.length, method: 0, dt: f.dt });
  }
  return writeZip(files);
}
